import { Component } from '@theme/component';
import { getBundledCartSectionIds } from './cart-section-ids.js';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Sets the loading state of the add to cart button.
   * @param {boolean} loading - Whether the button is in loading state.
   */
  setLoading(loading) {
    if (loading) {
      this.refs.addToCartButton.setAttribute('data-loading', 'true');
    } else {
      this.refs.addToCartButton.removeAttribute('data-loading');
    }
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) {
      return;
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }

    // Na CL-morph volgt native submit niet betrouwbaar. Roep addToCart() aan — niet
    // handleSubmit(clickEvent): component.js wrapt clicks in een Proxy.
    const productForm = this.closest('product-form-component');
    if (productForm && typeof /** @type {any} */ (productForm).addToCart === 'function') {
      event.preventDefault();
      try {
        /** @type {any} */ (productForm).addToCart();
      } catch (error) {
        console.error('addToCart from click failed', error);
      }
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  if (!customElements.get('add-to-cart-component')) customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
export class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #isAddingToCart = false;

  /** @type {number | undefined} */
  #animationTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for add-to-cart success events (from configurator or other sources)
    document.addEventListener('addToCart:success', this.#onAddToCartSuccess, { signal });

    // Na combined-listing morph(main): form-node kan vervangen zijn zonder nieuwe connectedCallback
    document.addEventListener('hds:main-morphed', this.#onMainMorphed, { signal });

    // Host-level submit overleeft form-node replacement (morph). Capture + defaultPrevented
    // voorkomt dubbele handleSubmit als component.js on:submit wél vindt.
    this.addEventListener('submit', this.#onHostSubmit, { capture: true, signal });
    this.#ensureSubmitAttributes();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#isAddingToCart = false;
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
  }

  /**
   * Na morph/section-update: refs + on:submit attrs herstellen.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#ensureSubmitAttributes();
  }

  /**
   * Combined-listing morph(main): knop kan disabled blijven van #onVariantSelected.
   */
  #onMainMorphed = () => {
    const inMain = Boolean(document.querySelector('main')?.contains(this));
    // Alleen de form in de gemorphte main — niet sticky/andere product forms
    if (!inMain) return;

    queueMicrotask(() => {
      if (!this.isConnected) {
        return;
      }
      this.updatedCallback();
      this.resetAddToCartState();
    });
  };

  /**
   * Zorg dat declarative on:submit blijft staan (component.js + morph-safety).
   */
  #ensureSubmitAttributes() {
    if (!this.hasAttribute('on:submit')) {
      this.setAttribute('on:submit', '/handleSubmit');
    }

    const form = this.querySelector('form');
    if (form && !form.hasAttribute('on:submit')) {
      form.setAttribute('on:submit', '/handleSubmit');
    }
  }

  /**
   * Host capture-fallback als morph `on:submit` stripte of form-node wisselde.
   * @param {Event} event
   */
  #onHostSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    if (!this.contains(event.target)) return;
    // component.js (document capture) heeft al gehandeld
    if (event.defaultPrevented) return;
    this.handleSubmit(event);
  };

  /** Reset UI state after quick-add modal morph / reopen / combined-listing morph. */
  resetAddToCartState() {
    this.#isAddingToCart = false;
    this.refs.addToCartButtonContainer?.setLoading(false);
    this.refs.addToCartButtonContainer?.enable();
  }

  /**
   * Public ATC entry (click) — geen Event/Proxy nodig.
   */
  addToCart() {
    try {
      this.#performAddToCart();
    } catch (error) {
      console.error('addToCart failed', error);
      this.#isAddingToCart = false;
      this.refs.addToCartButtonContainer?.setLoading(false);
      this.refs.addToCartButtonContainer?.enable();
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    try {
      event.preventDefault();
    } catch {
      // Proxy/event quirks
    }
    this.#performAddToCart(event);
  }

  /**
   * Shared cart/add fetch — gebruikt door submit-handler en click→addToCart().
   * @param {Event} [performanceEvent]
   */
  #performAddToCart(performanceEvent) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    if (this.#isAddingToCart) {
      return;
    }

    const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (button?.disabled) {
      return;
    }

    this.#isAddingToCart = true;
    this.refs.addToCartButtonContainer?.disable();
    this.refs.addToCartButtonContainer?.setLoading(true);

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);

    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    // Configurator: theme click→addToCart() slaat submit over. Delegeer naar nested-cart flow
    // zodat Lengte/Breedte + productopties als aparte cartregels meegaan.
    const hasConfigurator = Boolean(
      form.querySelector('input[name*="_configurator_"], input[name*="properties[_configurator_"]')
    );
    const nestedCart = /** @type {any} */ (window).ConfiguratorNestedCart;
    if (
      hasConfigurator &&
      nestedCart &&
      typeof nestedCart.addConfiguratorProductFromForm === 'function'
    ) {
      Promise.resolve(nestedCart.addConfiguratorProductFromForm(form))
        .catch((error) => {
          console.error('Configurator addToCart failed', error);
        })
        .finally(() => {
          this.#isAddingToCart = false;
          this.refs.addToCartButtonContainer?.setLoading(false);
          this.refs.addToCartButtonContainer?.enable();
        });
      return;
    }

    const formData = new FormData(form);

    const cartItemComponentsSectionIds = getBundledCartSectionIds();
    if (cartItemComponentsSectionIds.length) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          const addToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
          if (addToCartButton) {
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          // Dispatch success event for animation (handled by #onAddToCartSuccess)
          const successEvent = new CustomEvent('addToCart:success', {
            bubbles: true,
            detail: {
              productId: this.dataset.productId,
              form: form
            }
          });
          document.dispatchEvent(successEvent);

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error('performAddToCart error', error);
      })
      .finally(() => {
        this.#isAddingToCart = false;
        const buttonContainer = this.refs.addToCartButtonContainer;
        buttonContainer?.setLoading(false);
        buttonContainer?.enable();
        try {
          cartPerformance.measureFromEvent('add:user-action', performanceEvent ?? new Event('submit'));
        } catch {
          // performanceEvent kan een Proxy zijn; negeer meetfouten
        }
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const eventProductId = event.detail.data.productId;
    const eventNewProduct = event.detail.data.newProduct;

    if (eventNewProduct) {
      this.dataset.productId = eventNewProduct.id;
    } else if (eventProductId && eventProductId !== this.dataset.productId) {
      return;
    }

    // Refs kunnen stale zijn vlak na morph(main) — forceer refresh
    this.updatedCallback();

    const { variantId, addToCartButtonContainer } = this.refs;
    const currentAddToCartButton = addToCartButtonContainer?.refs?.addToCartButton;

    // Scoped: niet de sticky-ATC-knop (die staat eerder in de HTML met dezelfde ref)
    const scopedBtn = event.detail.data.html?.querySelector?.('product-form-component [ref="addToCartButton"]');
    const anyBtn = event.detail.data.html?.querySelector?.('[ref="addToCartButton"]');
    const newAddToCartButton = scopedBtn ?? anyBtn;

    // Eerst enable/disable — nooit early-return vóór dit, anders blijft knop disabled na CL-morph
    const available = Boolean(event.detail.resource?.available);
    if (!event.detail.resource || !available) {
      addToCartButtonContainer?.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer?.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    this.#isAddingToCart = false;
    addToCartButtonContainer?.setLoading(false);

    if (currentAddToCartButton && newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
      // Morph kan disabled opnieuw zetten vanuit sticky/source — herstel beschikbaarheid
      if (available) {
        addToCartButtonContainer?.enable();
      } else {
        addToCartButtonContainer?.disable();
      }
    }

    if (variantId && event.detail.resource) {
      variantId.value = event.detail.resource.id ?? '';
    }

    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };

  /**
   * Handles add-to-cart success events (from configurator or other sources)
   * Triggers the success animation on the add-to-cart button
   * @param {CustomEvent} event - The addToCart:success event
   */
  #onAddToCartSuccess = (event) => {
    // Only handle if this event is for this product form
    const productId = event.detail?.productId;
    if (productId && productId !== this.dataset.productId) {
      return;
    }

    const addToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (!addToCartButton) return;

    // Clear any existing animation timeout
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);

    // Show success animation
    addToCartButton.setAttribute('data-added', 'true');

    // Remove animation after 3 seconds
    this.#animationTimeout = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
    }, 3000);
  };
}

if (!customElements.get('product-form-component')) {
  if (!customElements.get('product-form-component')) customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  if (!customElements.get('fly-to-cart')) customElements.define('fly-to-cart', FlyToCart);
}
